import {v4 as uuidv4} from 'uuid';
import {PICKER_FAILED, POPUP_POLL_MS, type PickedFile} from './cloudConnect';

/**
 * Microsoft's OneDrive / SharePoint File Picker v8, hosted by Microsoft in a popup and
 * driven over postMessage + a MessagePort. Protocol: learn.microsoft.com/onedrive/developer/
 * controls/file-pickers (initialize → activate → acknowledged commands: authenticate / pick / close).
 */
const GRAPH = 'https://graph.microsoft.com/v1.0';

type PickedItem = {id: string; name?: string; parentReference?: {driveId?: string}};
type Command = {command?: string; resource?: string; items?: PickedItem[]};
type PortMessage = {type?: string; id?: string; data?: Command};
type PickerPort = Pick<MessagePort, 'postMessage' | 'start' | 'close' | 'addEventListener' | 'removeEventListener'>;
/** `resource` absent → the default (Graph) token; else a token for that SharePoint origin. */
export type PickerTokenSource = (resource?: string) => Promise<string>;

const graphGet = async <T>(path: string, token: string): Promise<T> => {
	const res = await fetch(`${GRAPH}${path}`, {headers: {Authorization: `Bearer ${token}`}});
	if (!res.ok) throw new Error(PICKER_FAILED);
	return (await res.json()) as T;
};

const listen = (popup: Window, channelId: string, tokenFor: PickerTokenSource): Promise<PickedFile | null> =>
	new Promise<PickedFile | null>((resolve, reject) => {
		let port: PickerPort | null = null;
		// A refusal leaves the picker open (it can still browse what it has tokens for); if the user
		// then gives up, the last refusal is why, so it surfaces (e.g. cloud_auth_revoked → Reconnect).
		let lastError: unknown = null;
		const reply = (id: string, data: Record<string, unknown>) => port?.postMessage({type: 'result', id, data});
		const timers: {poll?: ReturnType<typeof setInterval>} = {};
		let stopListening = () => undefined as void;
		const finish = (settle: () => void) => {
			stopListening();
			clearInterval(timers.poll);
			port?.close();
			popup.close();
			settle();
		};
		const giveUp = () => finish(() => (lastError ? reject(lastError) : resolve(null)));

		const nameOf = async (driveId: string, item: PickedItem): Promise<string | undefined> => {
			if (item.name) return item.name;
			try {
				const path = `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(item.id)}?$select=name`;
				// A fresh Graph token: the picker may have been open longer than the first one lives.
				return (await graphGet<{name?: string}>(path, await tokenFor())).name;
			} catch (error) {
				lastError = error;
				return undefined;
			}
		};

		const onPortMessage = async (event: MessageEvent) => {
			const message = event.data as PortMessage;
			if (message?.type !== 'command' || !message.id) return;
			const id = message.id;
			// Every command must be acknowledged before it is answered.
			port?.postMessage({type: 'acknowledge', id});
			const command = message.data ?? {};
			switch (command.command) {
				case 'authenticate':
					try {
						reply(id, {result: 'token', token: await tokenFor(command.resource)});
					} catch (error) {
						lastError = error;
						reply(id, {result: 'error', error: {code: 'unableToObtainToken', message: 'token unavailable'}});
					}
					return;
				case 'pick': {
					const item = command.items?.[0];
					const driveId = item?.parentReference?.driveId;
					if (!item || !driveId) {
						reply(id, {result: 'error', error: {code: 'unusableItem', message: 'missing drive id'}});
						return;
					}
					// Never link an asset under its opaque item id; refuse the pick and let the user retry.
					const name = await nameOf(driveId, item);
					if (!name) {
						reply(id, {result: 'error', error: {code: 'unusableItem', message: 'name unavailable'}});
						return;
					}
					reply(id, {result: 'success'});
					finish(() => resolve({externalFileId: `${driveId}/${item.id}`, name}));
					return;
				}
				case 'close':
					giveUp();
					return;
				default:
					reply(id, {result: 'error', error: {code: 'unsupportedCommand', message: String(command.command)}});
			}
		};

		// Only our own popup, on our random channel; after that everything is on the private port.
		const onWindowMessage = (event: MessageEvent) => {
			if (event.source !== popup) return;
			const data = event.data as {type?: string; channelId?: string};
			if (data?.type !== 'initialize' || data.channelId !== channelId || !event.ports?.[0]) return;
			// The picker page can reload and initialize again: retire the old port first.
			port?.removeEventListener('message', onPortMessage as EventListener);
			port?.close();
			port = event.ports[0];
			port.addEventListener('message', onPortMessage as EventListener);
			port.start();
			port.postMessage({type: 'activate'});
		};

		window.addEventListener('message', onWindowMessage);
		stopListening = () => {
			window.removeEventListener('message', onWindowMessage);
			port?.removeEventListener('message', onPortMessage as EventListener);
		};
		timers.poll = setInterval(() => {
			if (popup.closed) giveUp();
		}, POPUP_POLL_MS);
	});

const loadPicker = async (popup: Window, getToken: PickerTokenSource): Promise<PickedFile | null> => {
	const graphToken = await getToken();
	// The picker's own URL is the user's OneDrive for Business origin, and only Graph knows it
	// before the picker loads (its `authenticate` requests come after). v1 is work/school only.
	const drive = await graphGet<{webUrl?: string}>('/me/drive?$select=webUrl', graphToken);
	if (!drive.webUrl) throw new Error(PICKER_FAILED);
	// The workspace validates this origin (INVALID_PICKER_RESOURCE); no browser-side allow-list.
	const resource = new URL(drive.webUrl).origin;
	const channelId = uuidv4();
	const options = {
		sdk: '8.0',
		entry: {oneDrive: {files: {}}},
		// Present (even empty) = the host supplies tokens through `authenticate` commands.
		authentication: {},
		messaging: {origin: window.location.origin, channelId},
		typesAndSources: {mode: 'files', pivots: {oneDrive: true, recent: true, shared: true}},
		selection: {mode: 'single'},
	};
	// ponytail: pass the user's Forecast locale once someone asks for a localized picker.
	const query = new URLSearchParams({filePicker: JSON.stringify(options), locale: 'en-us'}).toString();
	const action = `${resource}/_layouts/15/FilePicker.aspx?${query}`;
	const accessToken = await getToken(resource);
	// Closed while we were loading: that is a cancel, and a closed window has no document to post into.
	if (popup.closed) return null;

	const form = popup.document.createElement('form');
	form.setAttribute('action', action);
	form.setAttribute('method', 'POST');
	const tokenInput = popup.document.createElement('input');
	tokenInput.setAttribute('type', 'hidden');
	tokenInput.setAttribute('name', 'access_token');
	tokenInput.setAttribute('value', accessToken);
	form.appendChild(tokenInput);
	popup.document.body.appendChild(form);
	form.submit();

	return listen(popup, channelId, (asked?: string) =>
		// The picker asks per SharePoint origin; a Graph (or unspecified) ask gets the default token.
		getToken(!asked || asked.startsWith('https://graph.microsoft.com') ? undefined : asked)
	);
};

/**
 * Resolves the picked file ({externalFileId: `${driveId}/${itemId}`, name}), or null when the user
 * closes the picker. The popup opens before anything is awaited, so call this from the click itself.
 */
export function pickOneDriveFile(getToken: PickerTokenSource): Promise<PickedFile | null> {
	// '_blank', never a fixed name: a named window left open on SharePoint would be handed back
	// cross-origin, and touching its document would throw.
	const popup = window.open('', '_blank', 'width=1080,height=680');
	if (!popup) return Promise.reject(new Error(PICKER_FAILED));
	// Only the open has to happen inside the click; the rest runs once the handler has returned.
	return Promise.resolve()
		.then(() => loadPicker(popup, getToken))
		.catch(error => {
			popup.close();
			throw error;
		});
}
